import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { Badge, Banner, Button, Card, Field, Input, PageHeader, Spinner } from "@magarac/ui";
import {
  customerDetailSchema,
  customerDisplayName,
  customerPhoneSchema,
  customerResponseSchema,
  propertyResponseSchema,
} from "@magarac/shared/schemas/customer";
import { jobPageSchema } from "@magarac/shared/schemas/job";
import {
  AddressFields,
  EMPTY_ADDRESS,
  isAddressComplete,
  toAddress,
} from "../../../components/AddressFields";
import { AssetsPanel } from "../../../components/AssetsPanel";
import { EditPropertyForm } from "../../../components/EditDialogs";
import {
  ArchiveCustomerDialog,
  MergeCustomerDialog,
  RestoreCustomerButton,
} from "../../../components/CustomerLifecycle";
import { HelpIcon } from "../../../components/HelpIcon";
import { NewJobDialog } from "../../../components/NewJobDialog";
import { NotesPanel } from "../../../components/NotesPanel";
import { RecurringPanel } from "../../../components/RecurringPanel";
import { Timeline } from "../../../components/Timeline";
import { api } from "../../../lib/api";
import { messageFor } from "../../../lib/errors";
import {
  addressLine,
  directionsUrl,
  formatLocalDate,
  formatMoney,
  formatPhone,
  JOB_STATUS_LABEL,
  telUrl,
} from "../../../lib/format";
import { useIsOffice, useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/customers/$id")({
  component: CustomerPage,
});

/** CRM-1, CRM-2, CRM-5, CRM-6, CRM-14, CRM-15. Screen key: customers.detail */
function CustomerPage() {
  const { id } = Route.useParams();
  const { active } = useTenant();
  const office = useIsOffice();
  const queryClient = useQueryClient();
  const key = ["customers", active?.id, id];
  const detail = useQuery({
    queryKey: key,
    queryFn: () => api.get(`/customers/${id}`, customerDetailSchema),
    enabled: Boolean(active),
  });
  const jobs = useQuery({
    queryKey: ["jobs", active?.id, "customer", id],
    queryFn: () => api.get("/jobs", jobPageSchema, { query: { customerId: id, limit: 50 } }),
    enabled: Boolean(active),
  });
  const [editing, setEditing] = useState(false);
  const [newJob, setNewJob] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [merging, setMerging] = useState(false);
  const [addingProperty, setAddingProperty] = useState(false);
  const [editingProperty, setEditingProperty] = useState<string | null>(null);
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [label, setLabel] = useState("");
  const [gate, setGate] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });

  const addProperty = useMutation({
    mutationFn: () =>
      api.post(
        `/customers/${id}/properties`,
        {
          address: toAddress(address),
          ...(label ? { label } : {}),
          ...(gate ? { gateCode: gate } : {}),
        },
        propertyResponseSchema,
      ),
    onSuccess: async () => {
      setAddingProperty(false);
      setAddress(EMPTY_ADDRESS);
      setLabel("");
      setGate("");
      await refresh();
    },
    onError: (err) => setError(messageFor(err)),
  });
  const addPhone = useMutation({
    mutationFn: () =>
      api.post(`/customers/${id}/phones`, { phone }, z.object({ phone: customerPhoneSchema })),
    onSuccess: async () => {
      setPhone("");
      await refresh();
    },
    onError: (err) => setError(messageFor(err)),
  });
  const removePhone = useMutation({
    mutationFn: (phoneId: string) =>
      api.delete(`/customers/${id}/phones/${phoneId}`, z.object({ ok: z.literal(true) })),
    onSuccess: refresh,
  });

  // Disabled until the business is known, so "not loading" does not mean "loaded".
  if (detail.error) return <Banner tone="danger">{messageFor(detail.error)}</Banner>;
  if (!detail.data) return <Spinner />;
  const d = detail.data;
  const c = d.customer;
  // CRM-29: archived customers are read-only here (the server refuses changes anyway).
  const archived = Boolean(c.archivedAt);
  const canEdit = office && !archived;

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title={customerDisplayName(c)}
        subtitle={c.company && c.firstName !== c.company ? c.company : undefined}
        help={<HelpIcon screen="customers.detail" />}
        actions={
          canEdit ? (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setNewJob(true)}>New job</Button>
              <Button variant="secondary" onClick={() => setMerging(true)}>
                Merge a duplicate
              </Button>
              <Button variant="ghost" onClick={() => setArchiving(true)}>
                Archive
              </Button>
            </div>
          ) : office && archived && !c.mergedIntoId ? (
            <RestoreCustomerButton customer={c} />
          ) : null
        }
      />
      {c.mergedIntoId ? (
        <Banner tone="info">
          This record was merged into another customer.{" "}
          <Link
            to="/customers/$id"
            params={{ id: c.mergedIntoId }}
            className="font-semibold underline"
          >
            Open the customer to use
          </Link>
        </Banner>
      ) : archived ? (
        <Banner tone="warn">
          Archived on {formatLocalDate(c.archivedAt!.slice(0, 10))}. Their history is kept; they
          take no new work until restored.
        </Banner>
      ) : null}
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <div className="grid gap-4 md:grid-cols-2">
        <Card title="Contact">
          {editing ? (
            <EditContact
              customer={c}
              onDone={async () => {
                setEditing(false);
                await refresh();
              }}
            />
          ) : (
            <>
              <ul className="space-y-1 text-base">
                {c.primaryPhone ? (
                  <li>
                    <a className="text-brand-600 underline" href={telUrl(c.primaryPhone)}>
                      {formatPhone(c.primaryPhone)}
                    </a>
                  </li>
                ) : null}
                {d.phones.map((p) => (
                  <li key={p.id} className="flex items-center gap-2">
                    <a className="text-brand-600 underline" href={telUrl(p.phone)}>
                      {formatPhone(p.phone)}
                    </a>
                    {p.label ? <span className="text-sm text-muted">{p.label}</span> : null}
                    {canEdit ? (
                      <button
                        type="button"
                        className="text-sm text-muted underline"
                        onClick={() => removePhone.mutate(p.id)}
                      >
                        remove
                      </button>
                    ) : null}
                  </li>
                ))}
                {c.email ? (
                  <li>
                    <a className="text-brand-600 underline" href={`mailto:${c.email}`}>
                      {c.email}
                    </a>
                  </li>
                ) : null}
              </ul>
              <div className="mt-2 flex flex-wrap gap-1">
                {c.tags.map((t) => (
                  <Badge key={t} tone="info">
                    {t}
                  </Badge>
                ))}
                {c.balanceCents > 0 ? (
                  <Badge tone="warn">Owes {formatMoney(c.balanceCents)}</Badge>
                ) : null}
                {c.smsOptOut ? <Badge>Texts off: replied STOP (gets emails)</Badge> : null}
              </div>
              {canEdit ? (
                <div className="mt-3 flex flex-wrap items-end gap-2">
                  <div className="flex-1">
                    <label htmlFor="add-phone" className="sr-only">
                      Add another phone
                    </label>
                    <Input
                      id="add-phone"
                      type="tel"
                      placeholder="Add another phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => addPhone.mutate()}
                    disabled={phone.trim().length < 4}
                    busy={addPhone.isPending}
                  >
                    Add
                  </Button>
                  <Button variant="ghost" onClick={() => setEditing(true)}>
                    Edit details
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </Card>

        <Card title="Addresses">
          {d.properties.length === 0 ? <p className="text-muted">No address yet.</p> : null}
          <ul className="space-y-4">
            {d.properties.map((p) => (
              <li key={p.id} className="border-b border-line pb-3 last:border-0">
                <div className="font-medium">
                  {p.label ? `${p.label}: ` : ""}
                  {addressLine(p.address)}
                </div>
                <div className="text-sm text-muted">
                  {[p.gateCode ? `Gate code ${p.gateCode}` : null, p.accessNotes]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
                {p.geocodeStatus === "failed" ? (
                  <p className="text-sm text-warn">
                    Could not find this on the map, so drive times use an estimate. Check the
                    address{canEdit ? " with Edit" : ""}.
                  </p>
                ) : null}
                {editingProperty === p.id ? (
                  <EditPropertyForm property={p} onDone={() => setEditingProperty(null)} />
                ) : canEdit ? (
                  <button
                    type="button"
                    className="mr-3 text-sm text-brand-600 underline"
                    onClick={() => setEditingProperty(p.id)}
                  >
                    Edit
                  </button>
                ) : null}
                <a
                  className="text-sm text-brand-600 underline"
                  href={directionsUrl(p.address)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Directions
                </a>
                <AssetsPanel propertyId={p.id} readOnly={!canEdit} />
              </li>
            ))}
          </ul>
          {canEdit ? (
            addingProperty ? (
              <div className="mt-3 rounded-lg border border-line p-3">
                <AddressFields idPrefix="cp-addr" value={address} onChange={setAddress} />
                <div className="grid gap-x-3 md:grid-cols-2">
                  <Field id="cp-label" label="Label (optional)" hint='Like "Rental on Elm St"'>
                    <Input id="cp-label" value={label} onChange={(e) => setLabel(e.target.value)} />
                  </Field>
                  <Field id="cp-gate" label="Gate code (optional)">
                    <Input id="cp-gate" value={gate} onChange={(e) => setGate(e.target.value)} />
                  </Field>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="secondary" onClick={() => setAddingProperty(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => addProperty.mutate()}
                    disabled={!isAddressComplete(address)}
                    busy={addProperty.isPending}
                  >
                    Save address
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="ghost" className="mt-2" onClick={() => setAddingProperty(true)}>
                Add an address
              </Button>
            )
          ) : null}
        </Card>

        <Card title="Jobs" className="md:col-span-2">
          {jobs.data?.items.length === 0 ? <p className="text-muted">No jobs yet.</p> : null}
          <ul className="divide-y divide-line">
            {jobs.data?.items.map((j) => (
              <li key={j.id}>
                <Link
                  to="/jobs/$id"
                  params={{ id: j.id }}
                  className="flex flex-wrap items-center justify-between gap-2 py-3 hover:bg-brand-50"
                >
                  <span>
                    <span className="font-medium">Job #{j.number}</span> · {j.jobTypeName}
                    {j.dueOn ? (
                      <span className="text-sm text-muted"> · due {formatLocalDate(j.dueOn)}</span>
                    ) : null}
                  </span>
                  <Badge tone={JOB_STATUS_LABEL[j.status].tone}>
                    {JOB_STATUS_LABEL[j.status].label}
                  </Badge>
                </Link>
              </li>
            ))}
          </ul>
        </Card>

        {canEdit ? (
          <div className="md:col-span-2">
            <RecurringPanel customerId={id} properties={d.properties} />
          </div>
        ) : null}
        <NotesPanel parentType="customer" parentId={id} readOnly={archived} />
        <Timeline customerId={id} />
      </div>
      {canEdit ? (
        <>
          <NewJobDialog open={newJob} onClose={() => setNewJob(false)} customerId={id} />
          <ArchiveCustomerDialog
            open={archiving}
            onClose={() => setArchiving(false)}
            customer={c}
            name={customerDisplayName(c)}
          />
          <MergeCustomerDialog
            open={merging}
            onClose={() => setMerging(false)}
            customer={c}
            name={customerDisplayName(c)}
          />
        </>
      ) : null}
    </div>
  );
}

function EditContact({
  customer,
  onDone,
}: {
  customer: {
    id: string;
    firstName: string;
    lastName: string | null;
    company: string | null;
    primaryPhone: string | null;
    email: string | null;
    tags: string[];
    updatedAt: string;
  };
  onDone: () => void | Promise<void>;
}) {
  const [firstName, setFirstName] = useState(customer.firstName);
  const [lastName, setLastName] = useState(customer.lastName ?? "");
  const [company, setCompany] = useState(customer.company ?? "");
  const [phone, setPhone] = useState(
    customer.primaryPhone ? formatPhone(customer.primaryPhone) : "",
  );
  const initialPhone = customer.primaryPhone ? formatPhone(customer.primaryPhone) : "";
  const [email, setEmail] = useState(customer.email ?? "");
  const [tags, setTags] = useState(customer.tags.join(", "));
  const [error, setError] = useState<string | null>(null);
  // Send only what was changed, so two people editing different fields never undo each other;
  // the server refuses the save if the customer changed since this form opened.
  const changedFields = () => {
    const next = {
      firstName,
      lastName: lastName || null,
      company: company || null,
      primaryPhone: phone || null,
      email: email || null,
      tags: tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    };
    const out: Record<string, unknown> = { expectedUpdatedAt: customer.updatedAt };
    if (next.firstName !== customer.firstName) out.firstName = next.firstName;
    if (next.lastName !== customer.lastName) out.lastName = next.lastName;
    if (next.company !== customer.company) out.company = next.company;
    if (next.email !== customer.email) out.email = next.email;
    if (next.tags.join(",") !== customer.tags.join(",")) out.tags = next.tags;
    if (phone !== initialPhone) out.primaryPhone = next.primaryPhone;
    return out;
  };
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/customers/${customer.id}`, changedFields(), customerResponseSchema),
    onSuccess: () => onDone(),
    onError: (err) => setError(messageFor(err)),
  });
  return (
    <div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <div className="grid gap-x-3 md:grid-cols-2">
        <Field id="ec-first" label="First name">
          <Input id="ec-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </Field>
        <Field id="ec-last" label="Last name">
          <Input id="ec-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </Field>
      </div>
      <Field id="ec-company" label="Company">
        <Input id="ec-company" value={company} onChange={(e) => setCompany(e.target.value)} />
      </Field>
      <Field id="ec-phone" label="Main phone">
        <Input id="ec-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </Field>
      <Field id="ec-email" label="Email">
        <Input
          id="ec-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
      <Field id="ec-tags" label="Tags" hint="Separate with commas">
        <Input id="ec-tags" value={tags} onChange={(e) => setTags(e.target.value)} />
      </Field>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={() => void onDone()}>
          Cancel
        </Button>
        <Button onClick={() => save.mutate()} busy={save.isPending} disabled={!firstName.trim()}>
          Save
        </Button>
      </div>
    </div>
  );
}
